// @owners { team: patients-team }
import { COLORS, SPACING } from '@alto/design-library-tokens';
import {
  ActionSheetContext,
  Description,
  InlineAlert,
  LgPadding,
  Link,
  Toast,
  ToastContext,
  XsSpacing,
} from '@alto/design-system';
import { type PickupData } from '@alto/scriptdash/alto/patient_app/checkout/v1/checkout_endpoint';
import React, { useContext } from 'react';
import { Linking } from 'react-native';
import { formatAddressToMapLink } from '~shared/helpers/address';
import { getPlatformOS } from '~shared/helpers/getPlatformOS';
import { PharmacyHoursActionSheet } from './PharmacyHoursActionSheet';
import { formatStoreTime } from './utils';

type Props = {
  pickupData?: PickupData | null;
};

export const PickupInfo = ({ pickupData }: Props) => {
  const { addToast } = useContext(ToastContext);
  const { setActiveActionSheet } = useContext(ActionSheetContext);

  if (!pickupData) {
    return null;
  }

  const {
    facility_address,
    facility_weekday_store_open,
    facility_weekday_store_close,
    facility_weekend_store_open,
    facility_weekend_store_close,
  } = pickupData;

  const today = new Date().getDay();
  const storeOpenTime = today === 0 || today === 6 ? facility_weekend_store_open : facility_weekday_store_open;
  const storeCloseTime = today === 0 || today === 6 ? facility_weekend_store_close : facility_weekday_store_close;

  const handleOpenMap = () => {
    const storeAddressLink = formatAddressToMapLink({ address: facility_address, isFacility: true });
    const platform = getPlatformOS();

    Linking.canOpenURL(storeAddressLink)
      .then(() => {
        if (platform !== 'ios' && platform !== 'android') {
          return window.open(storeAddressLink, '_blank');
        } else {
          return Linking.openURL(storeAddressLink);
        }
      })
      .catch(() => {
        addToast(<Toast variant="error">Unable to open map app</Toast>);
      });
  };

  const handleSeeAllHours = () => {
    setActiveActionSheet(<PharmacyHoursActionSheet pickupData={pickupData} />);
  };

  return (
    <LgPadding
      topPadding={SPACING.STATIC.MD}
      bottomPadding={SPACING.STATIC.MD}
    >
      <InlineAlert type="neutral">
        <>
          <Description>Pickup address:</Description>
          <Link
            color={COLORS.TEXT_COLORS.SECONDARY_DARK}
            fontFamily="regular"
            onPress={handleOpenMap}
            textSize="mini"
            underline
          >
            {`Alto Pharmacy, ${facility_address}`}
          </Link>
          <XsSpacing />
          <Description>{`Open today: ${formatStoreTime(storeOpenTime)} - ${formatStoreTime(storeCloseTime)}`}</Description>
          {!!pickupData && (
            <Link
              onPress={handleSeeAllHours}
              textSize="mini"
            >
              See all hours
            </Link>
          )}
        </>
      </InlineAlert>
    </LgPadding>
  );
};
